import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import './Auth.css';

// ============================================================
// API CONFIGURATION
// ============================================================
const API_BASE = process.env.REACT_APP_API_URL || 'https://kubi-backend-production.up.railway.app';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const API_URL = API_BASE.endsWith('/api') ? API_BASE.slice(0, -4) : API_BASE;

// Main website URL
const WEBSITE_URL = 'https://knine.ai';

// ============================================================
// GOOGLE SIGN-IN BUTTON (loads GSI script on demand)
// ============================================================
function GoogleSignInButton({ onSuccess, onError, label = 'Sign in with Google' }) {
  const btnRef = useRef(null);
  const [loading, setLoading] = useState(false);

  const handleCredentialResponse = useCallback((response) => {
    setLoading(true);
    axios.post(`${API_URL}/api/auth/google`, { idToken: response.credential })
      .then(res => onSuccess(res.data))
      .catch(err => {
        onError(err.response?.data?.error || 'Google sign-in failed.');
      })
      .finally(() => setLoading(false));
  }, [onSuccess, onError]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;

    const initGoogle = () => {
      if (!window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredentialResponse,
      });
      window.google.accounts.id.renderButton(btnRef.current, {
        type: 'standard',
        shape: 'rectangular',
        theme: 'outline',
        text: label === 'Sign up with Google' ? 'signup_with' : 'signin_with',
        size: 'large',
        width: btnRef.current?.offsetWidth || 340,
      });
    };

    if (window.google?.accounts?.id) {
      initGoogle();
    } else {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initGoogle;
      document.head.appendChild(script);
    }
  }, [handleCredentialResponse, label]);

  if (!GOOGLE_CLIENT_ID) return null;

  return (
    <div className="auth-google-wrapper">
      <div ref={btnRef} className="auth-google-btn" />
      {loading && <div className="auth-google-loading">Connecting...</div>}
    </div>
  );
}

// ============================================================
// 2FA VERIFICATION STEP (shown when login returns requires2FA)
// ============================================================
function TwoFAVerifyStep({ twoFAToken, onSuccess, onBack }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await axios.post(`${API_URL}/api/auth/2fa/verify`, { twoFAToken, code: code.trim() });
      onSuccess(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Verification failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="auth-login-logo">
        <div className="auth-login-logo-icon" style={{ fontSize: 28 }}>&#128272;</div>
        <h2>Two-Factor Authentication</h2>
        <p>Enter the 6-digit code from your authenticator app, or a recovery code.</p>
      </div>

      <div className="auth-login-card">
        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="auth-field">
            <label htmlFor="totp-code">Verification Code</label>
            <input
              type="text"
              id="totp-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\s/g, ''))}
              placeholder="123456"
              required
              autoComplete="one-time-code"
              autoFocus
              maxLength={8}
              style={{ textAlign: 'center', letterSpacing: '0.3em', fontSize: 20 }}
            />
          </div>

          <button type="submit" className="auth-submit" disabled={loading}>
            {loading ? <span className="auth-submit-loading">Verifying...</span> : 'Verify'}
          </button>
        </form>

        <button type="button" onClick={onBack} className="auth-forgot-link" style={{ display: 'block', textAlign: 'center', marginTop: 16 }}>
          Back to Sign In
        </button>

        <div className="auth-company">
          A <span>Knock Knock AI</span> product
        </div>
      </div>
    </>
  );
}

// ============================================================
// ANIMATED BACKGROUND — Falling particles (circles, dots, paw emojis)
// ============================================================
function AnimatedBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationId;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Particle types: circle, dot, paw
    const TYPES = ['circle', 'circle', 'dot', 'dot', 'dot', 'paw'];
    const COLORS = [
      'rgba(30, 136, 229, 0.18)',   // blue
      'rgba(30, 136, 229, 0.12)',   // lighter blue
      'rgba(139, 92, 246, 0.14)',   // purple
      'rgba(59, 130, 246, 0.13)',   // sky blue
      'rgba(99, 102, 241, 0.1)',    // indigo
    ];

    const PARTICLE_COUNT = 45;

    const makeParticle = (startAbove) => {
      const type = TYPES[Math.floor(Math.random() * TYPES.length)];
      return {
        x: Math.random() * canvas.width,
        y: startAbove ? -(Math.random() * canvas.height * 0.5) : Math.random() * canvas.height,
        speed: 0.3 + Math.random() * 0.7,
        drift: (Math.random() - 0.5) * 0.4,
        size: type === 'paw' ? 14 + Math.random() * 10 : (type === 'circle' ? 6 + Math.random() * 14 : 2 + Math.random() * 4),
        opacity: type === 'paw' ? 0.12 + Math.random() * 0.08 : 0.08 + Math.random() * 0.15,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        type,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 0.5,
      };
    };

    const particles = Array.from({ length: PARTICLE_COUNT }, () => makeParticle(false));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((p) => {
        ctx.save();
        ctx.globalAlpha = p.opacity;

        if (p.type === 'paw') {
          ctx.translate(p.x, p.y);
          ctx.rotate((p.rotation * Math.PI) / 180);
          ctx.font = `${p.size}px serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('🐾', 0, 0);
        } else if (p.type === 'circle') {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size / 2, 0, Math.PI * 2);
          ctx.fillStyle = p.color;
          ctx.fill();
        } else {
          // dot
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size / 2, 0, Math.PI * 2);
          ctx.fillStyle = p.color;
          ctx.fill();
        }

        ctx.restore();

        // Fall downward
        p.y += p.speed;
        p.x += p.drift;
        p.rotation += p.rotationSpeed;

        // Reset when below viewport
        if (p.y > canvas.height + 30) {
          Object.assign(p, makeParticle(true));
          p.y = -(10 + Math.random() * 40);
        }
        // Wrap horizontally
        if (p.x < -30) p.x = canvas.width + 20;
        if (p.x > canvas.width + 30) p.x = -20;
      });

      animationId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="auth-bg-canvas" />;
}

// ============================================================
// SHARED NAVBAR
// ============================================================
function AuthNav({ activePage }) {
  return (
    <nav className="auth-navbar">
      <a href={WEBSITE_URL} className="auth-navbar-logo">
        <div className="auth-logo-icon">K9</div>
        <span className="auth-logo-text">kNine<span className="auth-logo-highlight">.ai</span></span>
      </a>

      <div className="auth-navbar-links">
        <a href={WEBSITE_URL} className="auth-nav-link">Home</a>
        <a href={`${WEBSITE_URL}/pricing`} className="auth-nav-link">Pricing</a>
        <a href={`${WEBSITE_URL}/comparisons`} className="auth-nav-link">Comparisons</a>
        <a href={`${WEBSITE_URL}/integrations`} className="auth-nav-link">Integrations</a>
        <Link
          to="/register"
          className={`auth-nav-link${activePage === 'register' ? ' active' : ''}`}
        >
          Free Trial
        </Link>
        <a href={`${WEBSITE_URL}/#contact`} className="auth-nav-link">Contact</a>
      </div>

      <div className="auth-navbar-actions">
        <Link
          to="/login"
          className={`auth-nav-btn${activePage === 'login' ? ' active' : ''}`}
        >
          Log In
        </Link>
        <Link to="/register" className="auth-nav-btn-primary">Start Free Trial</Link>
      </div>
    </nav>
  );
}

// ============================================================
// REGISTER PAGE — matches knine.ai Free Trial page
// ============================================================
function RegisterForm() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [email, setEmail]         = useState('');
  const [phone, setPhone]         = useState('');
  const [company, setCompany]     = useState('');
  const [role, setRole]           = useState('');
  const [password, setPassword]           = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [resending, setResending] = useState(false);
  const [emailExistsInfo, setEmailExistsInfo] = useState(null); // { email, verified }
  const { executeRecaptcha } = useGoogleReCaptcha();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!company.trim()) {
      setError('Company name is required');
      return;
    }

    if (phone.trim()) {
      const digits = phone.replace(/\D/g, '');
      if (digits.length < 10 || digits.length > 15) {
        setError('Please enter a valid phone number');
        return;
      }
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!executeRecaptcha) {
      setError('reCAPTCHA not ready. Please try again.');
      return;
    }

    setLoading(true);

    try {
      const captchaToken = await executeRecaptcha('register');
      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();

      const response = await axios.post(`${API_URL}/api/auth/register`, {
        name: fullName,
        email,
        password,
        companyName: company.trim(),
        role: role || undefined,
        phone: phone.trim() || undefined,
        captchaToken,
      });

      // Registration successful — show "check your email" screen
      setVerificationSent(true);

      // If email failed to send, show the error so user knows to resend
      if (!response.data.emailSent) {
        setError(response.data.message);
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === 'EMAIL_EXISTS') {
        setEmailExistsInfo({
          email: data.existingEmail || email,
          verified: data.emailVerified,
        });
      }
      setError(
        data?.error ||
        data?.message ||
        'Registration failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const ROLE_OPTIONS = [
    'Recruiter',
    'Hiring Manager',
    'Business Development',
    'Team Lead / Manager',
    'Executive',
    'Other',
  ];

  const FEATURES = [
    {
      icon: '🔍',
      color: 'blue',
      title: 'Full AI Search',
      desc: 'Access our 100M+ record database with natural language queries.',
    },
    {
      icon: '🧩',
      color: 'green',
      title: 'Chrome Extension',
      desc: 'Source contacts directly from LinkedIn and job boards.',
    },
    {
      icon: '📊',
      color: 'purple',
      title: 'Analytics Dashboard',
      desc: 'Track your sourcing metrics and credit usage in real-time.',
    },
    {
      icon: '🔗',
      color: 'teal',
      title: 'ATS/CRM Connection',
      desc: 'Connect your existing tools and push contacts directly.',
    },
    {
      icon: '🐾',
      color: 'amber',
      title: 'Cooper & Roxi AI',
      desc: 'Full access to both AI assistants for recruiting and BD sourcing.',
    },
    {
      icon: '👤',
      color: 'rose',
      title: 'Up to 2 Users',
      desc: 'Invite a teammate to try the platform alongside you.',
    },
  ];

  const handleResendVerification = async () => {
    setResending(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/auth/resend-verification`, { email });
      setError(res.data.message || 'Verification email sent!');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend. Please try again.');
    } finally {
      setResending(false);
    }
  };

  // ── Verification sent screen ──
  if (verificationSent) {
    return (
      <div className="auth-page">
        <AnimatedBackground />
        <div className="auth-bg-orb auth-bg-orb-1" />
        <div className="auth-bg-orb auth-bg-orb-2" />
        <div className="auth-bg-orb auth-bg-orb-3" />

        <AuthNav activePage="register" />

        <div className="auth-login-page">
          <div className="auth-login-inner">
            <div className="auth-login-logo">
              <div className="auth-login-logo-icon" style={{ fontSize: 28 }}>&#9993;</div>
              <h2>Check your email</h2>
              <p>We sent a verification link to <strong>{email}</strong></p>
            </div>

            <div className="auth-login-card">
              {error && <div className="auth-error">{error}</div>}
              <p style={{ color: '#4a5568', lineHeight: 1.6, margin: '0 0 20px', textAlign: 'center' }}>
                Click the link in the email to verify your account, then come back here to sign in.
              </p>
              <p style={{ color: '#718096', fontSize: 14, margin: '0 0 20px', textAlign: 'center' }}>
                Didn't get the email? Check your spam folder or click below to resend.
              </p>
              <button
                className="auth-submit"
                onClick={handleResendVerification}
                disabled={resending}
                style={{ marginBottom: 16, background: '#475569' }}
              >
                {resending ? 'Sending...' : 'Resend Verification Email'}
              </button>
              <p className="auth-switch">
                <Link to="/login">Go to Sign In</Link>
              </p>
              <div className="auth-company">
                A <span>Knock Knock AI</span> product
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <AnimatedBackground />
      <div className="auth-bg-orb auth-bg-orb-1" />
      <div className="auth-bg-orb auth-bg-orb-2" />
      <div className="auth-bg-orb auth-bg-orb-3" />

      <AuthNav activePage="register" />

      <div className="auth-register-page">
        {/* Hero */}
        <div className="auth-hero">
          <div className="auth-hero-badge">🐕 Free Trial</div>
          <h1 className="auth-hero-title">
            Take kNine for a <span className="auth-hero-title-accent">Walk</span>
          </h1>
          <p className="auth-hero-subtitle">
            Try our Professional plan free for 14 days. No credit card required.
            Full access to AI-powered sourcing — let Cooper &amp; Roxi show you what they can do.
          </p>
        </div>

        {/* Two-column body */}
        <div className="auth-register-body">
          {/* Left — Form */}
          <div className="auth-form-card">
            <h2 className="auth-card-title">Start Your Free Trial</h2>
            <p className="auth-card-subtitle">Get full access in under 2 minutes.</p>

            {error && <div className="auth-error">{error}</div>}

            {emailExistsInfo && (
              <div className="auth-exists-actions">
                <Link to="/login" className="auth-exists-btn auth-exists-btn-primary">
                  Sign In to Your Account
                </Link>
                {emailExistsInfo.verified === false && (
                  <button
                    type="button"
                    className="auth-exists-btn auth-exists-btn-secondary"
                    onClick={handleResendVerification}
                    disabled={resending}
                  >
                    {resending ? 'Sending...' : 'Resend Verification Email'}
                  </button>
                )}
                <Link to="/forgot-password" className="auth-exists-btn auth-exists-btn-secondary">
                  Reset Your Password
                </Link>
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="auth-row">
                <div className="auth-field">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Jane"
                    required
                    autoComplete="given-name"
                  />
                </div>
                <div className="auth-field">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Smith"
                    required
                    autoComplete="family-name"
                  />
                </div>
              </div>

              <div className="auth-field">
                <label htmlFor="email">Work Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jane@company.com"
                  required
                  autoComplete="email"
                />
              </div>

              <div className="auth-field">
                <label htmlFor="company">Company</label>
                <input
                  type="text"
                  id="company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Acme Recruiting"
                  required
                  autoComplete="organization"
                />
              </div>

              <div className="auth-field">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  autoComplete="tel"
                />
              </div>

              <div className="auth-field">
                <label htmlFor="role">Role</label>
                <select
                  id="role"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                >
                  <option value="">Select your role</option>
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              <div className="auth-row">
                <div className="auth-field">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    autoComplete="new-password"
                  />
                </div>
                <div className="auth-field">
                  <label htmlFor="confirmPassword">Confirm</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    autoComplete="new-password"
                  />
                </div>
              </div>

              <button type="submit" className="auth-submit" disabled={loading}>
                {loading
                  ? <span className="auth-submit-loading">Creating account…</span>
                  : 'Start My Free Trial'}
              </button>
            </form>

            <p className="auth-no-cc">No credit card required. Cancel anytime.</p>

            <p className="auth-terms">
              By signing up you agree to our{' '}
              <a href={`${WEBSITE_URL}/terms`} target="_blank" rel="noopener noreferrer">
                Terms of Service
              </a>
            </p>

            <p className="auth-switch">
              Already have an account? <Link to="/login">Sign in</Link>
            </p>

            <div className="auth-company">
              A <span>Knock Knock AI</span> product
            </div>
          </div>

          {/* Right — Features */}
          <div className="auth-features-panel">
            <h3 className="auth-features-panel-title">What's Included in Your Trial</h3>
            <div className="auth-features-grid">
              {FEATURES.map((f) => (
                <div key={f.title} className="auth-feature-card">
                  <div className={`auth-feature-icon-wrap ${f.color}`}>{f.icon}</div>
                  <div className="auth-feature-text">
                    <h4>{f.title}</h4>
                    <p>{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function RegisterPage() {
  return (
    <GoogleReCaptchaProvider reCaptchaKey="6LdRTW8sAAAAAOVnNpWPqXR_z_K7j_ru-f-bIast">
      <RegisterForm />
    </GoogleReCaptchaProvider>
  );
}

// ============================================================
// LOGIN PAGE
// ============================================================
export function LoginPage() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resending, setResending] = useState(false);
  const [twoFAToken, setTwoFAToken] = useState(null); // set when login requires 2FA
  const navigate = useNavigate();

  const handleResend = async () => {
    setResending(true);
    try {
      await axios.post(`${API_URL}/api/auth/resend-verification`, { email });
      setError('Verification email sent! Check your inbox.');
      setNeedsVerification(false);
    } catch (_) {
      // silent
    } finally {
      setResending(false);
    }
  };

  // Shared handler for successful auth (used by both login form and Google)
  const handleAuthSuccess = (data) => {
    if (data.requires2FA) {
      setTwoFAToken(data.twoFAToken);
      setError('');
      return;
    }
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    navigate('/roxi');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNeedsVerification(false);
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      handleAuthSuccess(response.data);
    } catch (err) {
      if (err.response?.data?.requiresVerification) {
        setNeedsVerification(true);
      }
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Login failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <AnimatedBackground />
      <div className="auth-bg-orb auth-bg-orb-1" />
      <div className="auth-bg-orb auth-bg-orb-2" />
      <div className="auth-bg-orb auth-bg-orb-3" />

      <AuthNav activePage="login" />

      <div className="auth-login-page">
        <div className="auth-login-inner">
          {/* 2FA verification step */}
          {twoFAToken ? (
            <TwoFAVerifyStep
              twoFAToken={twoFAToken}
              onSuccess={handleAuthSuccess}
              onBack={() => { setTwoFAToken(null); setError(''); }}
            />
          ) : (
            <>
              {/* Logo */}
              <div className="auth-login-logo">
                <div className="auth-login-logo-icon">K9</div>
                <h2>Welcome back</h2>
                <p>Sign in to your kNine.ai account</p>
              </div>

              {/* Card */}
              <div className="auth-login-card">
                {error && <div className="auth-error">{error}</div>}
                {needsVerification && (
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resending}
                    className="auth-submit"
                    style={{ marginBottom: 16, background: '#475569', fontSize: 14 }}
                  >
                    {resending ? 'Sending...' : 'Resend Verification Email'}
                  </button>
                )}

                <GoogleSignInButton
                  onSuccess={handleAuthSuccess}
                  onError={(msg) => setError(msg)}
                />

                {GOOGLE_CLIENT_ID && <div className="auth-divider"><span>or</span></div>}

                <form onSubmit={handleSubmit} className="auth-form">
                  <div className="auth-field">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      required
                      autoComplete="email"
                    />
                  </div>

                  <div className="auth-field">
                    <label htmlFor="password">Password</label>
                    <input
                      type="password"
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      autoComplete="current-password"
                    />
                  </div>

                  <div style={{ textAlign: 'right', marginTop: -4, marginBottom: 12 }}>
                    <Link to="/forgot-password" className="auth-forgot-link">Forgot password?</Link>
                  </div>

                  <button type="submit" className="auth-submit" disabled={loading}>
                    {loading
                      ? <span className="auth-submit-loading">Signing in…</span>
                      : 'Sign In'}
                  </button>
                </form>

                <p className="auth-switch">
                  Don't have an account? <Link to="/register">Start free trial</Link>
                </p>

                <div className="auth-company">
                  A <span>Knock Knock AI</span> product
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// VERIFY EMAIL PAGE — handles the verification link click
// ============================================================
export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying | success | already | error | expired
  const [errorMsg, setErrorMsg] = useState('');
  const [resending, setResending] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setErrorMsg('No verification token provided.');
      return;
    }

    axios.get(`${API_URL}/api/auth/verify-email?token=${token}`)
      .then(res => {
        if (res.data.alreadyVerified) {
          setStatus('already');
        } else {
          setStatus('success');
        }
      })
      .catch(err => {
        if (err.response?.data?.expired) {
          setStatus('expired');
        } else {
          setStatus('error');
        }
        setErrorMsg(err.response?.data?.error || 'Verification failed.');
      });
  }, [searchParams]);

  const handleResend = async () => {
    if (!resendEmail) return;
    setResending(true);
    try {
      await axios.post(`${API_URL}/api/auth/resend-verification`, { email: resendEmail });
      setErrorMsg('New verification email sent! Check your inbox.');
    } catch (_) {
      // silent
    } finally {
      setResending(false);
    }
  };

  const renderContent = () => {
    if (status === 'verifying') {
      return (
        <>
          <div className="auth-login-logo">
            <div className="auth-login-logo-icon" style={{ fontSize: 28 }}>&#8987;</div>
            <h2>Verifying your email...</h2>
            <p>Please wait a moment.</p>
          </div>
        </>
      );
    }

    if (status === 'success' || status === 'already') {
      return (
        <>
          <div className="auth-login-logo">
            <div className="auth-login-logo-icon" style={{ fontSize: 28, background: 'linear-gradient(135deg, #059669, #10b981)' }}>&#10003;</div>
            <h2>{status === 'already' ? 'Already Verified' : 'Email Verified!'}</h2>
            <p>Your email has been confirmed. You can now sign in to kNine.ai.</p>
          </div>
          <div className="auth-login-card">
            <button className="auth-submit" onClick={() => navigate('/login')}>
              Go to Sign In
            </button>
            <div className="auth-company">
              A <span>Knock Knock AI</span> product
            </div>
          </div>
        </>
      );
    }

    // error or expired
    return (
      <>
        <div className="auth-login-logo">
          <div className="auth-login-logo-icon" style={{ fontSize: 28, background: 'linear-gradient(135deg, #dc2626, #ef4444)' }}>!</div>
          <h2>{status === 'expired' ? 'Link Expired' : 'Verification Failed'}</h2>
          <p>{errorMsg}</p>
        </div>
        <div className="auth-login-card">
          <p style={{ color: '#4a5568', fontSize: 14, margin: '0 0 12px', textAlign: 'center' }}>
            Enter your email to receive a new verification link:
          </p>
          <div className="auth-field" style={{ marginBottom: 12 }}>
            <input
              type="email"
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
              placeholder="you@company.com"
              autoComplete="email"
            />
          </div>
          <button className="auth-submit" onClick={handleResend} disabled={resending || !resendEmail}>
            {resending ? 'Sending...' : 'Resend Verification Email'}
          </button>
          {errorMsg.includes('sent') && (
            <p style={{ color: '#059669', fontSize: 14, margin: '12px 0 0', textAlign: 'center' }}>{errorMsg}</p>
          )}
          <p className="auth-switch" style={{ marginTop: 16 }}>
            <Link to="/login">Back to Sign In</Link>
          </p>
          <div className="auth-company">
            A <span>Knock Knock AI</span> product
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="auth-page">
      <AnimatedBackground />
      <div className="auth-bg-orb auth-bg-orb-1" />
      <div className="auth-bg-orb auth-bg-orb-2" />
      <div className="auth-bg-orb auth-bg-orb-3" />

      <AuthNav activePage="login" />

      <div className="auth-login-page">
        <div className="auth-login-inner">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// FORGOT PASSWORD PAGE
// ============================================================
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(
        err.response?.data?.error ||
        'Something went wrong. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <AnimatedBackground />
      <div className="auth-bg-orb auth-bg-orb-1" />
      <div className="auth-bg-orb auth-bg-orb-2" />
      <div className="auth-bg-orb auth-bg-orb-3" />

      <AuthNav activePage="login" />

      <div className="auth-login-page">
        <div className="auth-login-inner">
          <div className="auth-login-logo">
            <div className="auth-login-logo-icon" style={{ fontSize: 28 }}>&#128274;</div>
            <h2>{sent ? 'Check your email' : 'Reset your password'}</h2>
            <p>
              {sent
                ? <>We sent a password reset link to <strong>{email}</strong></>
                : 'Enter your email and we\'ll send you a link to reset your password.'}
            </p>
          </div>

          <div className="auth-login-card">
            {error && <div className="auth-error">{error}</div>}

            {sent ? (
              <>
                <p style={{ color: '#4a5568', lineHeight: 1.6, margin: '0 0 20px', textAlign: 'center', fontSize: 14 }}>
                  Click the link in the email to choose a new password. The link expires in 1 hour.
                </p>
                <p style={{ color: '#718096', fontSize: 13, margin: '0 0 20px', textAlign: 'center' }}>
                  Didn't get it? Check your spam folder or try again.
                </p>
                <button
                  className="auth-submit"
                  onClick={() => setSent(false)}
                  style={{ marginBottom: 16, background: '#475569' }}
                >
                  Try a Different Email
                </button>
              </>
            ) : (
              <form onSubmit={handleSubmit} className="auth-form">
                <div className="auth-field">
                  <label htmlFor="reset-email">Email</label>
                  <input
                    type="email"
                    id="reset-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    required
                    autoComplete="email"
                    autoFocus
                  />
                </div>

                <button type="submit" className="auth-submit" disabled={loading}>
                  {loading
                    ? <span className="auth-submit-loading">Sending...</span>
                    : 'Send Reset Link'}
                </button>
              </form>
            )}

            <p className="auth-switch">
              Remember your password? <Link to="/login">Sign in</Link>
            </p>

            <div className="auth-company">
              A <span>Knock Knock AI</span> product
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// RESET PASSWORD PAGE — handles the reset link click
// ============================================================
export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        email,
        newPassword: password,
      });
      setSuccess(true);
    } catch (err) {
      const data = err.response?.data;
      if (data?.expired) {
        setError('This reset link has expired. Please request a new one.');
      } else {
        setError(data?.error || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Missing or invalid link
  if (!token || !email) {
    return (
      <div className="auth-page">
        <AnimatedBackground />
        <div className="auth-bg-orb auth-bg-orb-1" />
        <div className="auth-bg-orb auth-bg-orb-2" />
        <div className="auth-bg-orb auth-bg-orb-3" />

        <AuthNav activePage="login" />

        <div className="auth-login-page">
          <div className="auth-login-inner">
            <div className="auth-login-logo">
              <div className="auth-login-logo-icon" style={{ fontSize: 28, background: 'linear-gradient(135deg, #dc2626, #ef4444)' }}>!</div>
              <h2>Invalid Reset Link</h2>
              <p>This password reset link is invalid or incomplete.</p>
            </div>
            <div className="auth-login-card">
              <Link to="/forgot-password" className="auth-submit" style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
                Request a New Reset Link
              </Link>
              <p className="auth-switch" style={{ marginTop: 16 }}>
                <Link to="/login">Back to Sign In</Link>
              </p>
              <div className="auth-company">
                A <span>Knock Knock AI</span> product
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <AnimatedBackground />
      <div className="auth-bg-orb auth-bg-orb-1" />
      <div className="auth-bg-orb auth-bg-orb-2" />
      <div className="auth-bg-orb auth-bg-orb-3" />

      <AuthNav activePage="login" />

      <div className="auth-login-page">
        <div className="auth-login-inner">
          <div className="auth-login-logo">
            <div className="auth-login-logo-icon" style={{ fontSize: 28 }}>
              {success ? '\u2713' : '\u{1F512}'}
            </div>
            <h2>{success ? 'Password Reset!' : 'Choose a New Password'}</h2>
            <p>
              {success
                ? 'Your password has been updated. You can now sign in.'
                : `Enter a new password for ${email}`}
            </p>
          </div>

          <div className="auth-login-card">
            {error && <div className="auth-error">{error}</div>}

            {success ? (
              <>
                <button className="auth-submit" onClick={() => navigate('/login')}>
                  Go to Sign In
                </button>
              </>
            ) : (
              <form onSubmit={handleSubmit} className="auth-form">
                <div className="auth-field">
                  <label htmlFor="new-password">New Password</label>
                  <input
                    type="password"
                    id="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimum 8 characters"
                    required
                    minLength={8}
                    autoComplete="new-password"
                    autoFocus
                  />
                </div>

                <div className="auth-field">
                  <label htmlFor="confirm-new-password">Confirm Password</label>
                  <input
                    type="password"
                    id="confirm-new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                </div>

                <button type="submit" className="auth-submit" disabled={loading}>
                  {loading
                    ? <span className="auth-submit-loading">Resetting...</span>
                    : 'Reset Password'}
                </button>

                {error && error.includes('expired') && (
                  <Link
                    to="/forgot-password"
                    className="auth-submit"
                    style={{ display: 'block', textAlign: 'center', textDecoration: 'none', marginTop: 12, background: '#475569' }}
                  >
                    Request a New Reset Link
                  </Link>
                )}
              </form>
            )}

            <p className="auth-switch" style={{ marginTop: 16 }}>
              <Link to="/login">Back to Sign In</Link>
            </p>

            <div className="auth-company">
              A <span>Knock Knock AI</span> product
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// ACCEPT INVITATION PAGE
// Reached via the link in the team invitation email.
// Lets the invited user set their name and password, then
// creates their account and redirects to login.
// ============================================================
export function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const token = searchParams.get('token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Please enter your full name.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/team/accept-invite`, { token, name: name.trim(), password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="auth-page">
        <AnimatedBackground />
        <div className="auth-bg-orb auth-bg-orb-1" />
        <div className="auth-bg-orb auth-bg-orb-2" />
        <div className="auth-bg-orb auth-bg-orb-3" />
        <AuthNav activePage="login" />
        <div className="auth-login-page">
          <div className="auth-login-inner">
            <div className="auth-login-logo">
              <div className="auth-login-logo-icon" style={{ fontSize: 28, background: 'linear-gradient(135deg, #dc2626, #ef4444)' }}>!</div>
              <h2>Invalid Invitation Link</h2>
              <p>This invitation link is missing required information. Please use the exact link from your invitation email.</p>
            </div>
            <div className="auth-login-card">
              <p className="auth-switch"><Link to="/login">Back to Sign In</Link></p>
              <div className="auth-company">A <span>Knock Knock AI</span> product</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <AnimatedBackground />
      <div className="auth-bg-orb auth-bg-orb-1" />
      <div className="auth-bg-orb auth-bg-orb-2" />
      <div className="auth-bg-orb auth-bg-orb-3" />
      <AuthNav activePage="login" />
      <div className="auth-login-page">
        <div className="auth-login-inner">
          <div className="auth-login-logo">
            <div className="auth-login-logo-icon" style={{ fontSize: 28 }}>
              {success ? '✓' : '👋'}
            </div>
            <h2>{success ? 'Account Created!' : 'Accept Your Invitation'}</h2>
            <p>
              {success
                ? 'Your account is ready. Sign in to get started.'
                : 'Set up your name and password to join your team.'}
            </p>
          </div>

          <div className="auth-login-card">
            {error && <div className="auth-error">{error}</div>}

            {success ? (
              <button className="auth-submit" onClick={() => navigate('/login')}>
                Go to Sign In
              </button>
            ) : (
              <form onSubmit={handleSubmit} className="auth-form">
                <div className="auth-field">
                  <label htmlFor="invite-name">Full Name</label>
                  <input
                    id="invite-name"
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Your full name"
                    autoFocus
                    required
                  />
                </div>
                <div className="auth-field">
                  <label htmlFor="invite-password">Password</label>
                  <input
                    id="invite-password"
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Min 8 chars, uppercase, lowercase, number"
                    required
                  />
                </div>
                <div className="auth-field">
                  <label htmlFor="invite-confirm">Confirm Password</label>
                  <input
                    id="invite-confirm"
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Repeat your password"
                    required
                  />
                </div>
                <button type="submit" className="auth-submit" disabled={loading}>
                  {loading ? 'Creating Account…' : 'Create Account & Join Team'}
                </button>
              </form>
            )}

            <p className="auth-switch" style={{ marginTop: 16 }}>
              <Link to="/login">Already have an account? Sign In</Link>
            </p>
            <div className="auth-company">A <span>Knock Knock AI</span> product</div>
          </div>
        </div>
      </div>
    </div>
  );
}
